import React, { useRef, useState } from "react";
import "./importStrukture.css";
import Button from "../../elements/button";
import Loading from "../../loading";
import {
  validateImportFileLocally,
  postImportFile,
  importErrorMessage,
  downloadImportReport,
} from "../../../utils/importApi";

const STATUS_FILTERS = [
  "Sve",
  "NOVO",
  "VEĆ POSTOJI",
  "UPOZORENJE",
  "KONFLIKT",
  "GREŠKA",
  "INFO",
];

export const statusClass = (status) => {
  switch (status) {
    case "NOVO":
      return "status-novo";
    case "VEĆ POSTOJI":
      return "status-postoji";
    case "UPOZORENJE":
      return "status-upozorenje";
    case "KONFLIKT":
      return "status-konflikt";
    case "GREŠKA":
      return "status-greska";
    default:
      return "status-info";
  }
};

/**
 * Popup za uvoz organizacione strukture iz Excela (samo Administrator).
 * Korak 1: izbor fajla; korak 2: „Proveri" prikazuje šta će se uvesti;
 * korak 3: „Uvezi" upisuje. Uvoz ništa ne menja dok se ne potvrdi.
 */
export default function ImportStrukture({ onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null); // { summary, rows, canCommit }
  const [filter, setFilter] = useState("Sve");
  const [acknowledged, setAcknowledged] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [created, setCreated] = useState(null);
  const inputRef = useRef();

  const handleFile = (e) => {
    setError("");
    setPreview(null);
    setCreated(null);
    setFile(e.target.files?.[0] || null);
  };

  const handlePreview = async () => {
    const localError = validateImportFileLocally(file);
    if (localError) {
      setError(localError);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const data = await postImportFile("/import/structure/preview", file);
      setPreview(data);
      setFilter("Sve");
      setAcknowledged(false);
    } catch (err) {
      setPreview(null);
      setError(importErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await postImportFile("/import/structure", file);
      setCreated(data.created);
      onImported?.();
    } catch (err) {
      setError(importErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleReport = async () => {
    try {
      await downloadImportReport({
        rows: preview.rows,
        summary: preview.summary,
      });
    } catch (err) {
      setError(importErrorMessage(err));
    }
  };

  if (created) {
    return (
      <div className="loading">
        <div className="import-popup">
          <p className="import-popup-title">Uvoz je završen</p>
          <p className="import-popup-done">
            Uvezeno je {created.units} organizacionih jedinica,{" "}
            {created.designations} radnih mesta i {created.employees} zaposlenih.
            <br />
            Struktura na strani je osvežena.
          </p>
          <div className="import-popup-actions">
            <Button title="Zatvori" onClick={onClose} style={{ width: "140px" }} />
          </div>
        </div>
      </div>
    );
  }

  const rows =
    preview &&
    (filter === "Sve"
      ? preview.rows
      : preview.rows.filter((r) => r.status === filter));
  const hasWarnings =
    preview && preview.rows.some((r) => r.status === "UPOZORENJE");
  const canImport = preview && preview.canCommit && (!hasWarnings || acknowledged);

  return (
    <div className="loading">
      <div className="import-popup">
        <p className="import-popup-title">Uvoz strukture iz Excela</p>

        <div className="import-popup-file-row">
          <input
            ref={inputRef}
            className="import-popup-file-input"
            type="file"
            accept=".xlsx"
            onChange={handleFile}
          />
          {file && <span className="import-popup-file-name">{file.name}</span>}
        </div>
        <p className="import-popup-note">
          Šablon se preuzima dugmetom „PREUZMITE ŠABLON" na strani Struktura
          preduzeća. Uvoz ide u dva koraka: prvo „Proveri" pokaže šta će se
          uvesti, pa tek „Uvezi" upisuje. Postojeće jedinice, radna mesta i
          zaposleni se preskaču, ništa se ne briše.
        </p>
        {error && <p className="import-popup-error">{error}</p>}

        {preview && (
          <>
            <div className="import-popup-summary">
              <span>
                Jedinice: {preview.summary.units.new} novo,{" "}
                {preview.summary.units.existing} već postoji
              </span>
              <span>
                Radna mesta: {preview.summary.designations.new} novo,{" "}
                {preview.summary.designations.existing} već postoji
              </span>
              <span>
                Zaposleni: {preview.summary.employees.new} novo,{" "}
                {preview.summary.employees.existing} već postoji,{" "}
                {preview.summary.employees.merged} spojeno
              </span>
              {preview.summary.managers && (
                <span>
                  Rukovodioci jedinica: {preview.summary.managers.new} biće
                  upisano, {preview.summary.managers.existing} već upisano,{" "}
                  {preview.summary.managers.warnings} upozorenja
                </span>
              )}
            </div>

            <div className="import-popup-filters">
              {STATUS_FILTERS.map((s) => (
                <button
                  key={s}
                  type="button"
                  className={
                    filter === s
                      ? "import-popup-filter active"
                      : "import-popup-filter"
                  }
                  onClick={() => setFilter(s)}
                >
                  {s === "Sve"
                    ? `Sve (${preview.rows.length})`
                    : `${s} (${
                        preview.rows.filter((r) => r.status === s).length
                      })`}
                </button>
              ))}
            </div>

            <div className="import-popup-table-wrapper">
              <table className="import-popup-table">
                <thead>
                  <tr>
                    <th style={{ width: "150px" }}>Sheet</th>
                    <th style={{ width: "45px" }}>Red</th>
                    <th style={{ width: "110px" }}>Entitet</th>
                    <th style={{ width: "180px" }}>Naziv</th>
                    <th style={{ width: "110px" }}>Status</th>
                    <th>Poruka</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={`${r.sheet}-${r.row}-${i}`}>
                      <td>{r.sheet}</td>
                      <td>{r.row}</td>
                      <td>{r.entity}</td>
                      <td>{r.name}</td>
                      <td>
                        <span
                          className={`import-popup-status ${statusClass(r.status)}`}
                        >
                          {r.status}
                        </span>
                      </td>
                      <td>{r.message}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {hasWarnings && (
              <label className="import-popup-checkbox">
                <input
                  type="checkbox"
                  checked={acknowledged}
                  onChange={(e) => setAcknowledged(e.target.checked)}
                />
                Pregledao sam upozorenja i potvrđujem uvoz.
              </label>
            )}
            {!preview.canCommit && (
              <p className="import-popup-error">
                Dok postoje redovi sa statusom GREŠKA uvoz nije moguć; ispravite
                fajl i proverite ga ponovo.
              </p>
            )}
          </>
        )}

        <div className="import-popup-actions">
          <Button
            title="Proveri"
            onClick={handlePreview}
            disabled={!file || loading}
            style={{ width: "140px" }}
          />
          <Button
            title="Uvezi"
            onClick={handleImport}
            disabled={!canImport || loading}
            style={{ width: "140px" }}
          />
          {preview && (
            <Button
              title="Izveštaj provere (XLSX)"
              onClick={handleReport}
              disabled={loading}
              style={{ width: "190px" }}
            />
          )}
          <Button
            title="Otkaži"
            onClick={onClose}
            style={{ width: "140px" }}
          />
        </div>
      </div>
      <Loading isLoading={loading} />
    </div>
  );
}
